import { ReactElement, ReactNode, useMemo } from 'react';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import classes from './SystemAdminDashboard.module.css';
import { PersonGroupIcon, EnvelopeClosedIcon, TasklistIcon, PhoneIcon } from '@navikt/aksel-icons';
import { useGetSystemAdminDashboardMetricsQuery } from '../../../hooks/queries/useGetSystemAdminDashboardMetricsQuery';
import type { SystemAdminDashboardMetrics } from '../../../api/queries';
import { formatNumber } from '../../../utils/numberFormatters';

const PAGE_TITLE = 'System Dashboard';
const PAGE_DESCRIPTION = 'Oversikt over hele Trustpulse-systemet';
const PERCENTAGE_BASE = 100;
const DECIMAL_PLACES = 1;

type StatCardProps = {
  title: string;
  icon: ReactNode;
  value: number;
  meta?: string;
};

const StatCard = ({ title, icon, value, meta }: StatCardProps): ReactElement => (
  <ContainerCard>
    <ContainerCard.Title icon={icon}>{title}</ContainerCard.Title>
    <ContainerCard.Body className={classes.statBody}>
      <div className={classes.statValue}>{value}</div>
      {meta && <div className={classes.statMeta}>{meta}</div>}
    </ContainerCard.Body>
  </ContainerCard>
);

type SmsStats = {
  used: number;
  quota: number;
  remaining: number;
  percentageUsed: number;
};

const calculateSmsStats = (sms: { used: number; quota: number }): SmsStats => ({
  used: sms.used,
  quota: sms.quota,
  remaining: Math.max(0, sms.quota - sms.used),
  percentageUsed: sms.quota > 0
    ? Math.min(PERCENTAGE_BASE, Math.max(0, (sms.used / sms.quota) * PERCENTAGE_BASE))
    : 0,
});

type SmsStatItemProps = {
  label: string;
  value: number;
};

const SmsStatItem = ({ label, value }: SmsStatItemProps): ReactElement => (
  <div className={classes.smsStatItem}>
    <div className={classes.smsLabel}>{label}</div>
    <div className={classes.smsValue}>{formatNumber(value)}</div>
  </div>
);

type SmsUsageCardProps = {
  smsStats: SmsStats;
};

const SmsUsageCard = ({ smsStats }: SmsUsageCardProps): ReactElement => (
  <ContainerCard>
    <ContainerCard.Title icon={<PhoneIcon />}>SMS-bruk</ContainerCard.Title>
    <ContainerCard.Body>
      <div className={classes.smsContent}>
        <div className={classes.smsStats}>
          <SmsStatItem label="Brukt" value={smsStats.used} />
          <SmsStatItem label="Totalt kvote" value={smsStats.quota} />
          <SmsStatItem label="Gjenstående" value={smsStats.remaining} />
        </div>
        <div className={classes.progressBar}>
          <div className={classes.progressFill} style={{ width: `${smsStats.percentageUsed}%` }} />
        </div>
        <div className={classes.progressLabel}>
          {smsStats.percentageUsed.toFixed(DECIMAL_PLACES)}% brukt
        </div>
      </div>
    </ContainerCard.Body>
  </ContainerCard>
);

type DashboardHeaderProps = {
  title: string;
  description: string;
};

const DashboardHeader = ({ title, description }: DashboardHeaderProps): ReactElement => (
  <header className={classes.header}>
    <Heading as="h1">{title}</Heading>
    <Paragraph size="lg">{description}</Paragraph>
  </header>
);

type DashboardLayoutProps = {
  header: ReactElement;
  children: ReactNode;
};

const DashboardLayout = ({ header, children }: DashboardLayoutProps): ReactElement => (
  <div className={classes.container}>
    {header}
    {children}
  </div>
);

type StatCardsGridProps = {
  metrics: SystemAdminDashboardMetrics;
};

const StatCardsGrid = ({ metrics }: StatCardsGridProps): ReactElement => {
  const statCards = useMemo(
    () => [
      {
        title: 'Totalt antall kunder',
        icon: <PersonGroupIcon />,
        value: metrics.totalTenants,
        meta: `+${metrics.growth.tenantsLastMonth} siste måned`,
      },
      {
        title: 'Registrerte brukere',
        icon: <PersonGroupIcon />,
        value: metrics.totalUsers,
        meta: `+${metrics.growth.usersLastMonth} siste måned`,
      },
      {
        title: 'Team-medlemmer',
        icon: <PersonGroupIcon />,
        value: metrics.totalTeamMembers,
        meta: 'Inkluderer annonyme',
      },
      {
        title: 'Partner-brukere',
        icon: <PersonGroupIcon />,
        value: metrics.totalPartnerUsers,
      },
      {
        title: 'Fullførte undersøkelser',
        icon: <TasklistIcon />,
        value: metrics.surveys.completed,
      },
      {
        title: 'Planlagte undersøkelser',
        icon: <EnvelopeClosedIcon />,
        value: metrics.surveys.planned,
      },
    ],
    [metrics]
  );

  return (
    <div className={classes.statsGrid}>
      {statCards.map((card) => (
        <StatCard key={card.title} {...card} />
      ))}
    </div>
  );
};

export const SystemAdminDashboard = (): ReactElement => {
  const { data, isLoading, error } = useGetSystemAdminDashboardMetricsQuery();
  const header = <DashboardHeader title={PAGE_TITLE} description={PAGE_DESCRIPTION} />;

  if (isLoading) {
    return (
      <DashboardLayout header={header}>
        <Paragraph>Laster dashboard data...</Paragraph>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout header={header}>
        <Paragraph>Kunne ikke laste dashboard data. Vennligst prøv igjen senere.</Paragraph>
      </DashboardLayout>
    );
  }

  const smsStats = calculateSmsStats(data.sms);

  return (
    <DashboardLayout header={header}>
      <div className={classes.content}>
        <StatCardsGrid metrics={data} />

        <div className={classes.smsSection}>
          <SmsUsageCard smsStats={smsStats} />
        </div>

        <div className={classes.trendsSection}>
          <ContainerCard>
            <ContainerCard.Title>Vekst over tid</ContainerCard.Title>
            <ContainerCard.Body>
              <Paragraph>
                Grafer for vekst og bruk vil vises her når API-endepunkter er på plass.
              </Paragraph>
            </ContainerCard.Body>
          </ContainerCard>
        </div>
      </div>
    </DashboardLayout>
  );
};
